import { useCallback, useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { brandedThemeStyle } from '@/lib/brand-color';
import { verifyPin } from '@/lib/pinLock';
import { PinPad } from './PinPad';

/**
 * Probes a logo's pixels once, off-screen, to decide which backing it needs.
 * Transparent marks drawn in dark ink vanish on a dark surface (the lock's
 * default), and pale/white wordmarks vanish on a light one — so we sample the
 * opaque pixels' average luminance and flip to a dark plate only when the mark
 * is overwhelmingly light. Any failure (no logo, load error, CORS-tainted
 * canvas) falls back to the light plate that suits the overwhelming majority of
 * brand marks. The displayed <img> is never given crossOrigin, so display can
 * never break even when sampling can't run.
 */
function useLogoPlate(url?: string | null): 'light' | 'dark' {
    const [tone, setTone] = useState<'light' | 'dark'>('light');
    useEffect(() => {
        setTone('light');
        if (!url) return;
        let active = true;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            if (!active) return;
            try {
                const size = 64;
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                if (!ctx) return;
                ctx.drawImage(img, 0, 0, size, size);
                const { data } = ctx.getImageData(0, 0, size, size);
                let lumSum = 0;
                let count = 0;
                for (let i = 0; i < data.length; i += 4) {
                    if (data[i + 3] < 32) continue; // skip transparent pixels
                    lumSum += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
                    count += 1;
                }
                if (count > 0 && lumSum / count >= 0.62) setTone('dark');
            } catch { /* tainted canvas / cross-origin — keep the light default */ }
        };
        img.src = url;
        return () => { active = false; };
    }, [url]);
    return tone;
}

/**
 * The full-screen app-lock. Branded, dark, premium. Auto-verifies the moment
 * the last digit is entered — no submit button. Wrong PIN shakes + clears, with
 * a short escalating cooldown after several misses; a "Sign out instead" escape
 * is always present so a forgotten PIN never bricks the app.
 */
export function PinScreen({
    userId,
    length,
    displayName,
    avatarUrl,
    businessLogoUrl,
    businessName,
    primaryColor,
    onUnlock,
}: {
    userId: string;
    length: number;
    displayName?: string | null;
    avatarUrl?: string | null;
    businessLogoUrl?: string | null;
    businessName?: string | null;
    primaryColor?: string | null;
    onUnlock: () => void;
}) {
    const [value, setValue] = useState('');
    const [shake, setShake] = useState(false);
    const [busy, setBusy] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const [cooldown, setCooldown] = useState(0);
    const [exiting, setExiting] = useState(false);
    const verifyingRef = useRef(false);

    const firstName = (displayName ?? '').trim().split(/\s+/)[0] || '';
    const initials = (displayName ?? '').trim().split(/\s+/).map((s) => s[0]).join('').slice(0, 2).toUpperCase() || '🙂';
    const plate = useLogoPlate(businessLogoUrl);

    const onWrong = useCallback(() => {
        const n = attempts + 1;
        setAttempts(n);
        setShake(true);
        setBusy(true);
        const delay = n > 4 ? Math.min((n - 4) * 600, 5000) : 600;
        window.setTimeout(() => {
            setValue('');
            setShake(false);
            setBusy(false);
        }, delay);
        if (n > 4) {
            let left = Math.ceil(delay / 1000);
            setCooldown(left);
            const t = window.setInterval(() => { left -= 1; setCooldown(left); if (left <= 0) window.clearInterval(t); }, 1000);
        }
    }, [attempts]);

    // Auto-verify when the PIN is complete.
    useEffect(() => {
        if (value.length !== length || verifyingRef.current || busy) return;
        verifyingRef.current = true;
        void (async () => {
            const ok = await verifyPin(userId, value);
            verifyingRef.current = false;
            if (ok) {
                setExiting(true);
                window.setTimeout(onUnlock, 280);
            } else {
                onWrong();
            }
        })();
    }, [value, length, userId, busy, onUnlock, onWrong]);

    const signOut = async () => {
        try { await supabase.auth.signOut(); } catch { /* noop */ }
        window.location.href = '/auth';
    };

    return (
        <div
            // pointer-events-auto is load-bearing: the lock can engage while a
            // Radix dialog (e.g. the audio/recording viewer) is open, and Radix
            // sets `pointer-events: none` on <body>. Without this the PIN screen
            // renders on top but inherits `none` — visible yet untouchable.
            className={`fixed inset-0 z-[200] pointer-events-auto flex flex-col items-center justify-center bg-slate-950 text-white transition-opacity duration-300 ${exiting ? 'opacity-0' : 'opacity-100'}`}
            style={brandedThemeStyle(primaryColor ?? undefined)}
        >
            {/* Brand glow */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute left-1/2 top-1/4 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/30 blur-[90px]" />
            </div>

            <div className="relative flex flex-col items-center gap-6 px-6">
                {/* Company branding — the business logo on a branded oval plate. A
                    readable backing (light by default, dark for pale wordmarks)
                    keeps transparent logos from disappearing into the dark lock,
                    framed by a primary-tinted outline + soft brand glow. No name. */}
                {businessLogoUrl && (
                    <div className="relative mb-1">
                        {/* Soft brand halo — adds a coloured glow for vivid brands;
                            invisibly harmless when the brand colour is near-dark. */}
                        <div aria-hidden className="pointer-events-none absolute -inset-2.5 rounded-full bg-primary/30 blur-xl" />
                        {/* Outline ring: a primary-tinted gradient frame for the brand
                            colour, over a guaranteed light hairline so the oval reads
                            crisply even when the brand colour is itself near-black. */}
                        <div className="relative rounded-full bg-gradient-to-b from-primary/70 to-primary/20 p-[2px] shadow-[0_12px_34px_-10px_rgba(0,0,0,0.75)] ring-1 ring-white/15">
                            {/* Readable plate — light for ink logos, dark for pale
                                wordmarks — so any logo stays legible. */}
                            <div className={`flex items-center justify-center rounded-full px-6 py-3 ring-1 ${plate === 'dark' ? 'bg-slate-900 ring-white/10' : 'bg-white ring-black/5'}`}>
                                <img src={businessLogoUrl} alt={businessName ?? ''} className="h-9 max-w-[180px] rounded-lg object-contain" />
                            </div>
                        </div>
                    </div>
                )}

                <div className="relative">
                    {avatarUrl ? (
                        <img src={avatarUrl} alt="" className="h-20 w-20 rounded-full object-cover ring-2 ring-primary/60 ring-offset-2 ring-offset-slate-950" />
                    ) : (
                        <div className="h-20 w-20 rounded-full bg-primary/90 ring-2 ring-primary/60 ring-offset-2 ring-offset-slate-950 flex items-center justify-center text-2xl font-semibold">{initials}</div>
                    )}
                    <span className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-slate-900 ring-2 ring-slate-950 flex items-center justify-center">
                        <Lock className="h-3.5 w-3.5 text-white/80" />
                    </span>
                </div>

                <div className="text-center space-y-1">
                    <h1 className="text-xl font-semibold">{firstName ? `Welcome back, ${firstName}` : 'Welcome back'}</h1>
                    <p className="text-sm text-white/60">{cooldown > 0 ? `Try again in ${cooldown}s` : shake ? 'Wrong PIN — try again' : 'Enter your PIN to unlock'}</p>
                </div>

                <PinPad length={length} value={value} onChange={setValue} shake={shake} disabled={busy || exiting} />

                <button type="button" onClick={signOut} className="mt-2 text-xs text-white/50 hover:text-white/80 underline underline-offset-4">
                    Sign out instead
                </button>
            </div>
        </div>
    );
}
